import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ngMatSlider from '@angular/material/slider'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../state'

/** Shared functionalizations slider/input list for space modals */
@ngCore.Component({
  imports: [
    ngCommon.PercentPipe,
    ds.input.number.Component,
    ds.loader.Component,
    ds.notice.Component,
    ngMatSlider.MatSlider,
    ngMatSlider.MatSliderThumb,
  ],
  selector: 'atn-space-modal-functionalizations',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** Intrinsic value type enum */
  public readonly IntrinsicValueType = ents.core.common.value.types.Intrinsic

  /**
   * Style enum exposed to HTML
   *
   * @see {@link ents.core.display.Style}
   */
  public readonly StyleEnum = ents.core.display.Style

  /** @see {@link state.space.full.functions.Collection} */
  private readonly _spaceFunctionsEc = ngCore.inject(
    state.space.full.functions.Collection,
  )

  /** Raw functionalizations from the entity being edited */
  public readonly functionalizationsSg = ngCore.input.required<
    ents.extra.space.Functionalization[]
  >({
    alias: 'functionalizations',
  })

  /**
   *  Read-only state of the functionalizations
   *
   * @default false
   */
  public readonly readonlySg = ngCore.input(false, { alias: 'readonly' })

  /**
   *  Loading state of the functionalizations
   *
   * @default false
   */
  public readonly loadingSg = ngCore.input(false, { alias: 'loading' })

  /**
   * Functionalizations merged with space functions for display.
   * Shows all non-exclusive space functions, using 0% for any without an entry.
   */
  public readonly displayFunctionalizationsSg = ngCore.computed(
    (
      spaceFunctions = this._spaceFunctionsEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      functionalizations = this.functionalizationsSg(),
    ) => {
      const existingMap = new Map(
        functionalizations.map(f => [f.functionId, f.percent]),
      )
      return spaceFunctions
        .filter(func => !func.isExclusive)
        .map(func => ({
          code: func.code,
          functionId: func.id,
          name: func.name,
          percent: existingMap.get(func.id) ?? 0,
        }))
    },
  )

  /** {@see {@link ents.extra.space.Functionalization.isEmpty}} */
  public readonly isEmptySg = ngCore.computed(
    (functionalizations = this.functionalizationsSg()) =>
      ents.extra.space.Functionalization.isEmpty(functionalizations),
  )

  /** {@see {@link ents.extra.space.Functionalization.isFullyCoded}} */
  public readonly isFullyCodedSg = ngCore.computed(
    (functionalizations = this.functionalizationsSg()) =>
      ents.extra.space.Functionalization.isFullyCoded(functionalizations),
  )

  /** {@see {@link ents.extra.space.Functionalization.calculateTotal}} */
  public readonly totalFunctionalUsageSg = ngCore.computed(
    (functionalizations = this.functionalizationsSg()) =>
      ents.extra.space.Functionalization.calculateTotal(functionalizations),
  )

  /** Emitted when slider or number input changes */
  public readonly functionalizationChange =
    ngCore.output<ents.extra.space.Functionalization>()

  /** Handle slider change */
  public handleSliderChange(
    functionId: ents.extra.space.Function.Id,
    event: Event,
  ): void {
    const percent = (event.target as HTMLInputElement).valueAsNumber / 100
    this.functionalizationChange.emit({ functionId, percent })
  }

  /** Handle number input change */
  public handleInputChange(
    functionId: ents.extra.space.Function.Id,
    percent: number | undefined,
  ): void {
    this.functionalizationChange.emit({ functionId, percent: percent ?? 0 })
  }

  /** Format slider label as percentage */
  public readonly formatLabel = (value: number): string =>
    `${Math.round(value)}%`
}
